"use client";

import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { type ReactNode, useActionState, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronUp, Edit3, Eye, Save } from "lucide-react";
import { ArticleContentRenderer } from "@/components/site/article-content-renderer";
import { saveForumThreadBulletinAction, type ForumThreadBulletinActionState } from "@/lib/forum-actions";
import { cn } from "@/lib/utils";

const ArticleEditor = dynamic(
  () => import("@/components/admin/article-editor/lexical-article-editor").then((mod) => mod.ArticleEditor),
  { ssr: false },
);

const EMPTY_LEXICAL_CONTENT = {
  type: "lexical",
  editorState: {
    root: {
      type: "root",
      format: "",
      indent: 0,
      version: 1,
      children: [{ type: "paragraph", format: "", indent: 0, version: 1, children: [], direction: null }],
      direction: null,
    },
  },
};

type LexicalPayload = { type: "lexical"; editorState: unknown };

function lexicalNodeHasContent(node: unknown): boolean {
  if (typeof node !== "object" || node === null) return false;
  const raw = node as {
    type?: unknown;
    text?: unknown;
    src?: unknown;
    videoId?: unknown;
    embedUrl?: unknown;
    children?: unknown;
  };

  if (raw.type === "text") return typeof raw.text === "string" && raw.text.trim().length > 0;
  if (raw.type === "article-image") return typeof raw.src === "string" && raw.src.trim().length > 0;
  if (raw.type === "youtube-video") return typeof raw.videoId === "string" && raw.videoId.trim().length > 0;
  if (raw.type === "social-embed") return typeof raw.embedUrl === "string" && raw.embedUrl.trim().length > 0;

  return Array.isArray(raw.children) && raw.children.some(lexicalNodeHasContent);
}

function blockNoteNodeHasContent(node: unknown): boolean {
  if (typeof node === "string") return node.trim().length > 0;
  if (Array.isArray(node)) return node.some(blockNoteNodeHasContent);
  if (typeof node !== "object" || node === null) return false;

  const raw = node as { type?: unknown; text?: unknown; props?: unknown; content?: unknown; children?: unknown };
  if (typeof raw.text === "string" && raw.text.trim()) return true;
  if (raw.type === "articleImage" || raw.type === "videoEmbed" || raw.type === "socialEmbed") return true;

  return blockNoteNodeHasContent(raw.content) || blockNoteNodeHasContent(raw.children) || blockNoteNodeHasContent(raw.props);
}

function hasBulletinContent(content: unknown): boolean {
  if (!content) return false;
  if (typeof content === "string") return content.trim().length > 0;
  if (Array.isArray(content)) return content.some(blockNoteNodeHasContent);
  if (typeof content !== "object") return false;

  const raw = content as { type?: unknown; editorState?: { root?: unknown }; root?: unknown };
  if (raw.type === "lexical") return lexicalNodeHasContent(raw.editorState?.root);
  return lexicalNodeHasContent(raw.root) || blockNoteNodeHasContent(content);
}

function ThreadBulletinContent({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(compact ? 300 : 340);

  const measure = useCallback(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const nextContentHeight = Math.ceil(contentElement.scrollHeight);
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    const nextCollapsedHeight = isMobile ? (compact ? 230 : 250) : compact ? 300 : 340;

    setCollapsedHeight(nextCollapsedHeight);
    setHasMeasured(true);
    setHasOverflow(nextContentHeight > nextCollapsedHeight + 8);
  }, [compact]);

  useEffect(() => {
    measure();

    const contentElement = contentRef.current;
    if (!contentElement) return;

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(contentElement);
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const shouldConstrain = !expanded && (!hasMeasured || hasOverflow);

  const handleCollapse = useCallback(() => {
    const wrapperElement = wrapperRef.current;
    const wrapperTop = wrapperElement?.getBoundingClientRect().top ?? 0;

    setExpanded(false);

    if (wrapperElement && wrapperTop < 0) {
      requestAnimationFrame(() => {
        wrapperElement.scrollIntoView({ block: "start" });
      });
    }
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className={cn(
          "relative transition-[max-height] duration-500 ease-out",
          shouldConstrain ? "overflow-hidden" : "max-h-none overflow-visible",
        )}
        style={shouldConstrain ? { maxHeight: collapsedHeight } : undefined}
        aria-expanded={expanded}
      >
        <div ref={contentRef} className="flow-root">
          {children}
        </div>
      </div>

      {!expanded && hasOverflow ? (
        <>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-44 bg-gradient-to-t from-[#FCFBF8] via-[#FCFBF8]/95 via-45% to-transparent" />
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2 text-[#A7193F] transition-all duration-300 hover:translate-y-1 hover:text-[#7F1D1D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A7193F]/25"
            aria-expanded={expanded}
            aria-label="Közlöny kinyitása"
          >
            <ChevronDown className="h-9 w-9 stroke-[2.25]" aria-hidden="true" />
          </button>
        </>
      ) : null}

      {expanded && hasOverflow ? (
        <div className="mt-8 flex justify-center pb-2">
          <button
            type="button"
            onClick={handleCollapse}
            className="text-[#A7193F] transition-all duration-300 hover:-translate-y-1 hover:text-[#7F1D1D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A7193F]/25"
            aria-expanded={expanded}
            aria-label="Közlöny összecsukása"
          >
            <ChevronUp className="h-9 w-9 stroke-[2.25]" aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function ThreadBulletin({
  threadId,
  enabled,
  content,
  canEdit,
  compact = false,
}: {
  threadId: string;
  enabled: boolean;
  content: unknown;
  canEdit: boolean;
  compact?: boolean;
}) {
  const router = useRouter();
  const [isEditing, setIsEditing] = useState(false);
  const [draftEnabled, setDraftEnabled] = useState(enabled);
  const [editorBlockCount, setEditorBlockCount] = useState(0);
  const [serializationStatus, setSerializationStatus] = useState<"ok" | "error">("ok");
  const [isUploadingEditorImage, setIsUploadingEditorImage] = useState(false);
  const [state, formAction, isPending] = useActionState<ForumThreadBulletinActionState, FormData>(
    saveForumThreadBulletinAction,
    { ok: false },
  );
  const effectiveEnabled = state.ok && typeof state.bulletinEnabled === "boolean" ? state.bulletinEnabled : enabled;
  const effectiveContent = state.ok && state.contentBlocks !== undefined ? state.contentBlocks : content;
  const initialContent = useMemo(() => effectiveContent ?? EMPTY_LEXICAL_CONTENT, [effectiveContent]);
  const hasContent = useMemo(() => hasBulletinContent(effectiveContent), [effectiveContent]);
  const shouldShowPublicBulletin = draftEnabled && hasContent;
  const shouldShowEditor = canEdit && isEditing && (draftEnabled || hasContent);
  const shouldShowEmptyAdminControl = canEdit && !draftEnabled && !hasContent && !isEditing;
  const [contentBlocksJson, setContentBlocksJson] = useState(() => JSON.stringify(initialContent));

  useEffect(() => {
    const nextContentBlocksJson = JSON.stringify(initialContent);
    queueMicrotask(() => setContentBlocksJson(nextContentBlocksJson));
  }, [initialContent]);

  useEffect(() => {
    setDraftEnabled(effectiveEnabled);
  }, [effectiveEnabled]);

  useEffect(() => {
    if (!state.ok) return;
    queueMicrotask(() => setIsEditing(false));
    router.refresh();
  }, [router, state.ok, state.savedAt]);

  function handleBlocksChange(payload: LexicalPayload, blockCount: number) {
    try {
      setContentBlocksJson(JSON.stringify(payload));
      setEditorBlockCount(blockCount);
      setSerializationStatus("ok");
    } catch {
      setSerializationStatus("error");
    }
  }

  function handleDraftEnabledChange(checked: boolean) {
    setDraftEnabled(checked);
    if (checked) {
      setIsEditing(true);
      return;
    }
    if (!hasContent) setIsEditing(false);
  }

  if (!canEdit && !shouldShowPublicBulletin) return null;

  return (
    <section className={cn("border border-[#D8C99B]/60 bg-[#FCFBF8]/88", compact ? "mx-4 mt-4 px-4 py-5 sm:mx-5 sm:px-5" : "mt-8 px-5 py-6 sm:px-6")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h2 className="text-sm font-black uppercase tracking-[0.14em] text-[#273E47]">Thread közlöny</h2>
          <p className="mt-1 text-sm font-semibold leading-6 text-[#273E47]/65">
            Kiemelt információk, linkek, képek vagy szerkesztői közlések ehhez a beszélgetéshez.
          </p>
        </div>
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-2">
            {state.ok ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-emerald-800">
                <Check size={13} /> Mentve
              </span>
            ) : null}
            {(hasContent || draftEnabled) && !shouldShowEmptyAdminControl ? (
              <button
                type="button"
                onClick={() => setIsEditing((value) => !value)}
                className="inline-flex h-9 items-center gap-2 rounded-full border border-[#D8C99B]/70 bg-white px-3 text-xs font-black uppercase tracking-[0.12em] text-[#273E47] transition hover:border-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
              >
                {isEditing ? <Eye size={14} /> : <Edit3 size={14} />}
                {isEditing ? "Nézet" : "Szerkesztés"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {shouldShowEditor ? (
        <form action={formAction} className="mt-5 grid gap-4">
          <input type="hidden" name="threadId" value={threadId} readOnly />
          <input type="hidden" name="contentBlocks" value={contentBlocksJson} readOnly />
          <input type="hidden" name="contentSerializationStatus" value={serializationStatus} readOnly />
          <input type="hidden" name="contentSerializationBlockCount" value={editorBlockCount} readOnly />
          <label className="flex w-max items-center gap-3 text-sm font-black text-[#273E47]">
            <input
              type="checkbox"
              name="bulletinEnabled"
              checked={draftEnabled}
              onChange={(event) => handleDraftEnabledChange(event.target.checked)}
              className="h-5 w-5 rounded border-[#D8C99B] accent-[#A4243B]"
            />
            Thread közlöny aktív
          </label>
          <div className="overflow-visible border-y border-[#D8C99B]/45 bg-white/65 py-2">
            <div className="border-b border-[#D8C99B]/45 bg-[#F7F5EF]/95 px-2 py-2">
              <div
                id="article-editor-toolbar-slot"
                className="min-w-0 overflow-x-auto overflow-y-visible"
                aria-label="Közlöny szerkesztő eszköztár"
              />
            </div>
            <ArticleEditor
              initialContent={initialContent}
              onChange={handleBlocksChange}
              onImageUploadStateChange={setIsUploadingEditorImage}
              imageUploadSlug={`thread-bulletin-${threadId}`}
              placeholder="Írd a thread közlönyt..."
              canvasClassName="min-h-[280px]"
              rootClassName="mt-3"
            />
          </div>
          {state.error ? <p className="text-sm font-semibold text-[#A4243B]">{state.error}</p> : null}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isPending || isUploadingEditorImage || serializationStatus === "error"}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-[#A4243B] px-4 text-xs font-black uppercase tracking-[0.14em] text-white transition hover:bg-[#D8973C] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save size={15} />
              {isPending ? "Mentés..." : isUploadingEditorImage ? "Képfeltöltés..." : "Közlöny mentése"}
            </button>
          </div>
        </form>
      ) : shouldShowEmptyAdminControl ? (
        <div className="mt-5 border-y border-dashed border-[#D8C99B]/70 py-4">
          <label className="flex w-max items-center gap-3 text-sm font-black text-[#273E47]">
            <input
              type="checkbox"
              checked={draftEnabled}
              onChange={(event) => handleDraftEnabledChange(event.target.checked)}
              className="h-5 w-5 rounded border-[#D8C99B] accent-[#A4243B]"
            />
            Thread közlöny aktív
          </label>
          <p className="mt-2 text-sm font-semibold leading-6 text-[#273E47]/58">
            Ehhez a threadhez még nincs közlöny.
          </p>
        </div>
      ) : (
        <div className="mt-5">
          {shouldShowPublicBulletin ? (
            <ThreadBulletinContent compact={compact}>
              <ArticleContentRenderer blocks={effectiveContent} />
            </ThreadBulletinContent>
          ) : canEdit && !draftEnabled ? (
            <div className="border-y border-dashed border-[#D8C99B]/70 py-8 text-sm font-semibold leading-6 text-[#273E47]/58">
              A thread közlöny jelenleg inaktív. {hasContent ? "Van mentett tartalom, de public nézetben nem jelenik meg." : "Nincs még mentett tartalom."}
            </div>
          ) : canEdit ? (
            <div className="border-y border-dashed border-[#D8C99B]/70 py-8 text-sm font-semibold leading-6 text-[#273E47]/58">
              Nincs még közlöny ehhez a threadhez. Nyisd meg szerkesztésre, kapcsold aktívra, majd mentsd el.
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
